import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { hasEffectivePermission } from '../utils/accessControl';
import AppIcon from '../components/AppIcon';
import '../styles/Reports.css';

const API_BASE = API_BASE_URL;

const normalizeStatus = (value) => {
  const key = String(value || 'unknown').toLowerCase();
  if (key === 'in_progress') return 'Devam Eden';
  if (key === 'completed') return 'Tamamlanan';
  if (key === 'to_do') return 'Bekleyen';
  if (key === 'blocked') return 'Bloklu';
  if (key === 'pending') return 'Onay Bekleyen';
  return key === 'unknown' ? 'Bilinmeyen' : key;
};

const Reports = () => {
  const { user, permissions } = useAuth();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [puantaj, setPuantaj] = useState([]);
  const [dailyReports, setDailyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const canViewReports = hasEffectivePermission(user, permissions, 'can_view_reports');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectsRes, tasksRes, puantajRes, dailyRes] = await Promise.all([
          apiClient.get(`${API_BASE}/projects`),
          apiClient.get(`${API_BASE}/tasks`),
          apiClient.get(`${API_BASE}/puantaj`),
          apiClient.get(`${API_BASE}/dailyReports`),
        ]);

        setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
        setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
        setPuantaj(Array.isArray(puantajRes.data) ? puantajRes.data : []);
        setDailyReports(Array.isArray(dailyRes.data) ? dailyRes.data : []);
        setError('');
      } catch (requestError) {
        setError(requestError.response?.data?.message || 'Rapor verileri yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const projectSummary = useMemo(
    () =>
      projects.reduce((acc, item) => {
        const key = normalizeStatus(item.status);
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {}),
    [projects]
  );

  const taskSummary = useMemo(
    () =>
      tasks.reduce((acc, item) => {
        const key = normalizeStatus(item.status);
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {}),
    [tasks]
  );

  const puantajSummary = useMemo(() => {
    const totalHours = puantaj.reduce((sum, item) => sum + (parseFloat(item.hours_worked) || 0), 0);
    const pendingCount = puantaj.filter((item) => String(item.status).toLowerCase() === 'pending').length;
    return { totalHours, pendingCount };
  }, [puantaj]);

  const topWorkers = useMemo(() => {
    const workerMap = {};
    puantaj.forEach((entry) => {
      const key = entry.user_username || `Kullanıcı ${entry.user_id}`;
      workerMap[key] = (workerMap[key] || 0) + (parseFloat(entry.hours_worked) || 0);
    });
    return Object.entries(workerMap)
      .map(([name, hours]) => ({ name, hours }))
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 8);
  }, [puantaj]);

  if (!canViewReports) {
    return (
      <div className="reports-page">
        <div className="reports-error">Bu alana erişim yetkiniz yok.</div>
      </div>
    );
  }

  if (loading) {
    return <div className="reports-loading">Raporlar yükleniyor...</div>;
  }

  return (
    <div className="reports-page">
      <section className="reports-hero">
        <div className="reports-hero-title-wrap">
          <h2>Merkezi Raporlar</h2>
          <p>Proje, görev, puantaj ve günlük rapor akışlarını tek ekranda izleyin.</p>
        </div>
        <div className="reports-hero-icon-wrap" aria-hidden="true">
          <AppIcon name="reports" className="reports-hero-icon" />
        </div>
      </section>

      {error && <div className="reports-error">{error}</div>}

      <section className="reports-metric-grid">
        <MetricCard
          title="Toplam Proje"
          value={projects.length}
          subtitle={`Aktif: ${projectSummary['Devam Eden'] || 0}`}
          icon="projects"
        />
        <MetricCard
          title="Toplam Görev"
          value={tasks.length}
          subtitle={`Tamamlanan: ${taskSummary.Tamamlanan || 0}`}
          icon="tasks"
        />
        <MetricCard
          title="Toplam Puantaj Saati"
          value={puantajSummary.totalHours.toFixed(1)}
          subtitle={`Onay bekleyen: ${puantajSummary.pendingCount}`}
          icon="puantaj"
        />
        <MetricCard
          title="Günlük Rapor"
          value={dailyReports.length}
          subtitle="Tüm saha kayıtları"
          icon="dailyReports"
        />
      </section>

      <section className="reports-summary-grid">
        <SummaryPanel title="Proje Durum Dağılımı" data={projectSummary} />
        <SummaryPanel title="Görev Durum Dağılımı" data={taskSummary} />
      </section>

      <section className="reports-table-card">
        <h3>En Fazla Saat Girilen Personel</h3>
        {topWorkers.length === 0 ? (
          <div className="reports-empty">Kayıt bulunamadı.</div>
        ) : (
          <table className="reports-table">
            <thead>
              <tr>
                <th>Personel</th>
                <th>Toplam Saat</th>
              </tr>
            </thead>
            <tbody>
              {topWorkers.map((worker) => (
                <tr key={worker.name}>
                  <td data-label="Personel">{worker.name}</td>
                  <td data-label="Toplam Saat">{worker.hours.toFixed(1)}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

const MetricCard = ({ title, value, subtitle, icon }) => (
  <article className="reports-metric-card">
    <div className="reports-metric-icon-wrap" aria-hidden="true">
      <AppIcon name={icon} className="reports-metric-icon" />
    </div>
    <div>
      <div className="reports-metric-title">{title}</div>
      <div className="reports-metric-value">{value}</div>
      <div className="reports-metric-subtitle">{subtitle}</div>
    </div>
  </article>
);

const SummaryPanel = ({ title, data }) => {
  const entries = Object.entries(data);
  return (
    <article className="reports-summary-card">
      <h4>{title}</h4>
      {entries.length === 0 ? (
        <div className="reports-empty">Veri bulunamadı.</div>
      ) : (
        <div className="reports-summary-list">
          {entries.map(([key, value]) => (
            <div key={key} className="reports-summary-item">
              <span>{key}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      )}
    </article>
  );
};

export default Reports;
